import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loginUserTienda } from "../../services/LoginUserTienda";

function TiendaLogin() {
  const navigate = useNavigate();
  const [correo, setCorreo] = useState("");
  const [password, setPassword] = useState("");
  const [correoError, setCorreoError] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [btnHover, setBtnHover] = useState(false);
  const [screenWidth, setScreenWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const validateCorreo = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return "Ingresa tu correo electrónico.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      return "Ingresa un correo electrónico válido.";
    }
    return "";
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    const emailError = validateCorreo(correo);
    setCorreoError(emailError);
    if (emailError) return;

    if (!password.trim()) {
      setError("Ingresa tu contraseña.");
      return;
    }

    setLoading(true);

    try {
      const resultado = await loginUserTienda(correo, password);

      if (!resultado?.ok) {
        localStorage.removeItem("tienda_token");
        localStorage.removeItem("tienda_data");
        localStorage.removeItem("tienda_permisos");
        localStorage.removeItem("tienda_usuario_id");
        localStorage.removeItem("tienda_id");
        setError(resultado?.data?.message || "Credenciales inválidas.");
        return;
      }

      localStorage.setItem("newSession", "1");
      navigate("/administracion/tienda");
    } catch (err) {
      setError(err?.message || "Error inesperado al iniciar sesión.");
    } finally {
      setLoading(false);
    }
  };

  const device = useMemo(() => {
    if (screenWidth <= 480) return "mobile";
    if (screenWidth <= 768) return "tablet";
    return "desktop";
  }, [screenWidth]);

  const isMobile = device === "mobile";
  const isTablet = device === "tablet";

  const theme = {
    bg: "#f7f9ff",
    white: "#ffffff",

    text: "#182033",
    textSoft: "#5f6b85",
    textMuted: "#7e89a4",

    primary: "#6d5efc",
    primaryDark: "#4f46e5",
    secondary: "#4da3ff",

    border: "rgba(95, 108, 133, 0.14)",
    borderStrong: "rgba(109, 94, 252, 0.20)",

    cardBg: "rgba(255,255,255,0.88)",
    inputBg: "#fbfcff",
    inputBorder: "rgba(109, 94, 252, 0.12)",
    inputBorderFocus: "rgba(109, 94, 252, 0.32)",

    badgeBg: "rgba(255,255,255,0.82)",
    badgeText: "#4f46e5",

    errorBg: "#fff1f2",
    errorBorder: "#fecdd3",
    errorText: "#be123c",

    shadow: "0 24px 70px rgba(74, 85, 120, 0.14)",
    shadowSoft: "0 12px 30px rgba(74, 85, 120, 0.08)",
  };

  const sizes = {
    pagePadding: isMobile ? 14 : isTablet ? 20 : 24,
    cardMaxWidth: isMobile ? "100%" : "470px",
    cardPadding: isMobile ? "24px 18px" : isTablet ? "30px 24px" : "36px 32px",
    cardRadius: isMobile ? 20 : 26,
    titleSize: isMobile ? 24 : 28,
    subtitleSize: isMobile ? 13 : 14,
    bodySize: isMobile ? 13 : 14,
    inputSize: isMobile ? 13 : 14,
    inputPaddingY: isMobile ? 13 : 15,
    inputPaddingX: isMobile ? 14 : 16,
    buttonPadding: isMobile ? 13 : 15,
    footerSize: isMobile ? 11.5 : 12.5,
    logoSize: isMobile ? 52 : 58,
  };

  const btnBackground = btnHover
    ? "linear-gradient(135deg, #5a4ef4, #3f8fff)"
    : "linear-gradient(135deg, #6d5efc, #4da3ff)";
  const btnBoxShadow = btnHover
    ? "0 18px 34px rgba(77,163,255,0.22)"
    : "0 16px 30px rgba(109,94,252,0.18)";

  const styles = {
    page: {
      minHeight: "100dvh",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: sizes.pagePadding,
      position: "relative",
      overflow: "hidden",
      background: theme.bg,
      boxSizing: "border-box",
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
    },

    badge: {
      position: "absolute",
      top: isMobile ? 16 : 22,
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 2,
      padding: isMobile ? "8px 12px" : "10px 16px",
      borderRadius: 999,
      border: `1px solid ${theme.borderStrong}`,
      background: theme.badgeBg,
      color: theme.badgeText,
      fontSize: isMobile ? 10.5 : 11.5,
      fontWeight: 900,
      letterSpacing: "0.08em",
      backdropFilter: "blur(10px)",
      whiteSpace: "nowrap",
      boxShadow: theme.shadowSoft,
    },

    card: {
      width: "100%",
      maxWidth: sizes.cardMaxWidth,
      padding: sizes.cardPadding,
      borderRadius: sizes.cardRadius,
      background: theme.cardBg,
      border: `1px solid ${theme.border}`,
      boxShadow: theme.shadow,
      color: theme.text,
      backdropFilter: "blur(14px)",
      boxSizing: "border-box",
      zIndex: 2,
      animation: "cyraviAdminIn 0.55s ease",
      position: "relative",
    },

    logoWrap: {
      width: sizes.logoSize,
      height: sizes.logoSize,
      margin: "0 auto 16px",
      borderRadius: 18,
      background: "linear-gradient(135deg, rgba(109,94,252,0.12), rgba(77,163,255,0.12))",
      border: `1px solid ${theme.borderStrong}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 14px 32px rgba(109,94,252,0.10)",
    },

    logoText: {
      fontSize: isMobile ? 18 : 20,
      fontWeight: 900,
      background: "linear-gradient(135deg, #6d5efc, #4da3ff)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      letterSpacing: "-0.04em",
    },

    header: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      marginBottom: isMobile ? 18 : 22,
    },

    title: {
      margin: 0,
      fontSize: sizes.titleSize,
      fontWeight: 900,
      letterSpacing: "-0.03em",
      color: theme.text,
      lineHeight: 1.15,
    },

    tagline: {
      marginTop: 8,
      marginBottom: 12,
      fontSize: sizes.subtitleSize,
      fontWeight: 800,
      color: theme.primaryDark,
      letterSpacing: "0.03em",
    },

    sub: {
      margin: 0,
      fontSize: sizes.bodySize,
      fontWeight: 500,
      color: theme.textSoft,
      lineHeight: 1.55,
      maxWidth: 360,
    },

    formBody: {
      display: "flex",
      flexDirection: "column",
      gap: 2,
    },

    error: {
      background: theme.errorBg,
      color: theme.errorText,
      padding: "12px 13px",
      borderRadius: 14,
      marginBottom: 14,
      border: `1px solid ${theme.errorBorder}`,
      fontSize: 13,
      fontWeight: 700,
      boxShadow: "0 10px 22px rgba(190, 18, 60, 0.06)",
    },

    fieldGroup: {
      marginBottom: 14,
    },

    label: {
      fontSize: 13,
      fontWeight: 800,
      color: theme.textSoft,
      display: "block",
      marginBottom: 8,
      letterSpacing: "0.01em",
    },

    fieldError: {
      color: theme.errorText,
      fontSize: 12,
      fontWeight: 600,
      marginBottom: 6,
      lineHeight: 1.4,
    },

    inputWrap: {
      position: "relative",
    },

    inputAccent: {
      position: "absolute",
      left: 14,
      top: "50%",
      transform: "translateY(-50%)",
      width: 9,
      height: 9,
      borderRadius: 999,
      background: "linear-gradient(135deg, #6d5efc, #4da3ff)",
      boxShadow: "0 0 0 4px rgba(109,94,252,0.10)",
      pointerEvents: "none",
    },

    input: {
      width: "100%",
      boxSizing: "border-box",
      padding: `${sizes.inputPaddingY}px ${sizes.inputPaddingX}px ${sizes.inputPaddingY}px 34px`,
      borderRadius: 16,
      border: `1px solid ${theme.inputBorder}`,
      background: theme.inputBg,
      color: theme.text,
      fontSize: sizes.inputSize,
      outline: "none",
      boxShadow: "inset 0 1px 2px rgba(0,0,0,0.02)",
      transition:
        "border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease, background 0.18s ease",
    },

    btn: {
      width: "100%",
      padding: sizes.buttonPadding,
      marginTop: 12,
      border: "none",
      borderRadius: 16,
      cursor: loading ? "wait" : "pointer",
      opacity: loading ? 0.88 : 1,
      fontWeight: 900,
      letterSpacing: "0.02em",
      color: "#fff",
      fontSize: isMobile ? 13.5 : 14.5,
      background: btnBackground,
      boxShadow: btnBoxShadow,
      transform: btnHover ? "translateY(-1px)" : "translateY(0)",
      transition:
        "transform 0.16s ease, box-shadow 0.16s ease, background 0.16s ease",
    },

    footer: {
      marginTop: 18,
      paddingTop: 14,
      borderTop: "1px solid rgba(95,108,133,0.10)",
      textAlign: "center",
    },

    footerHint: {
      textAlign: "center",
      fontSize: sizes.footerSize,
      fontWeight: 600,
      color: theme.textMuted,
      lineHeight: 1.5,
    },

    footerMark: {
      marginTop: 6,
      textAlign: "center",
      fontSize: sizes.footerSize,
      fontWeight: 900,
      color: theme.primaryDark,
      letterSpacing: "0.04em",
    },
  };

  return (
    <div style={styles.page}>

      <form onSubmit={handleLogin} noValidate style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.title}>Panel de administración</h2>
          <div style={styles.tagline}>Cyravi Admin</div>
          <p style={styles.sub}>
            Inicia sesión para administrar tu tienda, productos, pedidos y configuración.
          </p>
        </div>

        <div style={styles.formBody}>
          {error && <div style={styles.error}>{error}</div>}

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Correo electrónico</label>
            {correoError && <div style={styles.fieldError}>{correoError}</div>}
            <div style={styles.inputWrap}>
              <span style={styles.inputAccent} />
              <input
                type="text"
                inputMode="email"
                autoComplete="email"
                value={correo}
                onChange={(e) => {
                  setCorreo(e.target.value);
                  if (correoError) setCorreoError("");
                }}
                style={styles.input}
                placeholder="Ingresa tu correo"
                onFocus={(e) => {
                  e.target.style.borderColor = theme.inputBorderFocus;
                  e.target.style.background = "#ffffff";
                  e.target.style.boxShadow =
                    "0 0 0 4px rgba(109,94,252,0.10), 0 12px 24px rgba(74,85,120,0.08)";
                  e.target.style.transform = "translateY(-1px)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = theme.inputBorder;
                  e.target.style.background = theme.inputBg;
                  e.target.style.boxShadow = "inset 0 1px 2px rgba(0,0,0,0.02)";
                  e.target.style.transform = "translateY(0)";
                }}
              />
            </div>
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Contraseña</label>
            <div style={styles.inputWrap}>
              <span style={styles.inputAccent} />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={styles.input}
                placeholder="Ingresa tu contraseña"
                onFocus={(e) => {
                  e.target.style.borderColor = theme.inputBorderFocus;
                  e.target.style.background = "#ffffff";
                  e.target.style.boxShadow =
                    "0 0 0 4px rgba(77,163,255,0.10), 0 12px 24px rgba(74,85,120,0.08)";
                  e.target.style.transform = "translateY(-1px)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = theme.inputBorder;
                  e.target.style.background = theme.inputBg;
                  e.target.style.boxShadow = "inset 0 1px 2px rgba(0,0,0,0.02)";
                  e.target.style.transform = "translateY(0)";
                }}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={styles.btn}
            onMouseEnter={() => !loading && setBtnHover(true)}
            onMouseLeave={() => setBtnHover(false)}
          >
            {loading ? "Conectando..." : "Iniciar sesión"}
          </button>

          <div style={{ textAlign: "center", marginTop: 14 }}>
            <button
              type="button"
              onClick={() => navigate("/administracion/tienda/recuperar-contrasena")}
              style={{
                background: "none",
                border: "none",
                color: theme.primaryDark,
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>
        </div>

        <div style={styles.footer}>
          <div style={styles.footerHint}>
            Acceso seguro para la gestión y operación de tu tienda.
          </div>
          <div style={styles.footerMark}>Cyravi by Nexus Bridge</div>
        </div>
      </form>

      <style>{`
        @keyframes cyraviAdminIn {
          from {
            opacity: 0;
            transform: translateY(14px) scale(0.985);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
}

export default TiendaLogin;